// ─────────────────────────────────────────────────────────────────────────────
// DoorlyPro partner-facing buyer statuses — PRESENTATION LAYER.
//
// The COPY (label, definition, nextStep, owner, note) now lives in the backend:
// backend/constants/partnerStatuses.js, served by GET /api/knowledge. That is
// what makes Eva and this portal describe a buyer's status identically — Eva
// voices the same sentences the partner is reading on screen.
//
// THIS FILE OWNS COLOR ONLY. Palette is presentation, not knowledge.
//
// MERGE RULE — never drop a status:
//   copy + color   → rendered with its palette entry
//   copy, no color → rendered with NEUTRAL_COLOR (a new backend status must
//                    still appear, just unstyled)
//   color, no copy → ignored (a palette entry for a retired status is dead)
//
// BOOTSTRAP_STATUSES is the copy as shipped, kept so the UI renders correctly
// before hydration completes and stays correct if /api/knowledge is unreachable.
// It is a fallback, never the source of truth — the backend wins whenever it
// answers.
//
// ⚠️ THE byPartnerType OVERRIDES ARE MIRRORED INTO THE BOOTSTRAP TOO, AND THAT IS
// NOT OPTIONAL. Without them a knowledge outage silently downgrades a realtor to
// the shared copy, which tells them to wait on the buyer for home selection —
// work that is theirs under v2.2 §2.1 and a condition of their fee under §4.2.
// A fallback that fails toward "do nothing" is worse than one that fails loudly.
//
// ⚠️ THE TEST THIS USED TO CITE DID NOT EXIST. This header named
// test/partnerStatusOverrides.test.js as the guard that "asserts the two copies
// agree"; no such file was ever written, and the only real check was a spot-test
// for one phrase. It would have passed while the two files disagreed about
// everything after that phrase. The guard is now
// backend/test/partnerStatusParity.test.js and it compares every field.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_COLORS = {
    "invited": {
      "bg": "#e5e7eb",
      "color": "#374151"
    },
    "received": {
      "bg": "#e5e7eb",
      "color": "#374151"
    },
    "pending_application": {
      "bg": "#dbeafe",
      "color": "#1e40af"
    },
    "pending_identity": {
      "bg": "#bfdbfe",
      "color": "#1e3a8a"
    },
    "pending_documents": {
      "bg": "#bfdbfe",
      "color": "#1e3a8a"
    },
    "pending_verification": {
      "bg": "#ffdd86",
      "color": "#111827"
    },
    "pending_credit": {
      "bg": "#bfdbfe",
      "color": "#1e3a8a"
    },
    "pending_decision": {
      "bg": "#ffdd86",
      "color": "#111827"
    },
    "final_review": {
      "bg": "#ffdd86",
      "color": "#111827"
    },
    "approved": {
      "bg": "#acf44e",
      "color": "#111827"
    },
    "offer_accepted": {
      "bg": "#99f6e4",
      "color": "#0f766e"
    },
    "under_contract": {
      "bg": "#3a6ad9",
      "color": "#ffffff"
    },
    "closing": {
      "bg": "#1e40af",
      "color": "#ffffff"
    },
    "closed": {
      "bg": "#111827",
      "color": "#ffffff"
    },
    "not_moving_forward": {
      "bg": "#ff082f",
      "color": "#ffffff"
    }
  };

const NEUTRAL_COLOR = { bg: '#e5e7eb', color: '#374151' };

const BOOTSTRAP_STATUSES = [
    {
      "key": "invited",
      "label": "Invite not yet accepted",
      "definition": "You've invited this buyer — the invite hasn't been accepted yet.",
      "nextStep": "Waiting on the buyer to accept the invite and register. They can start anytime; resend it if they need a nudge.",
      "owner": "buyer",
      "byPartnerType": {
        "loan_officer": {
          "definition": "You've sent this loan file — your borrower hasn't accepted the invite yet.",
          "nextStep": "Waiting on your borrower to accept the invite and claim the file. Nothing moves until they do; they were emailed an invitation when you sent it, and you can resend it.",
          "owner": "buyer"
        }
      }
    },
    {
      "key": "received",
      "label": "Received",
      "definition": "We've received the details you sent for this buyer.",
      "nextStep": "Nothing for you to do on this one.",
      "owner": "doorly",
      "byPartnerType": {
        "loan_officer": {
          "definition": "We've received the loan file you sent for this borrower.",
          "nextStep": "Nothing for you to do on this one.",
          "owner": "doorly"
        }
      }
    },
    {
      "key": "pending_application",
      "label": "Pending Application",
      "definition": "Buyer registered and pre-qualified; their full application is in progress.",
      "nextStep": "Waiting on the buyer to finish the ~5-minute application and upload docs — at their own pace.",
      "owner": "buyer",
      "byPartnerType": {
        "loan_officer": {
          "definition": "Your borrower registered; the application you sent is being confirmed and completed.",
          "nextStep": "Waiting on your borrower to confirm what you sent and finish anything still outstanding — at their own pace.",
          "owner": "buyer"
        }
      }
    },
    {
      "key": "pending_identity",
      "label": "Verifying Identity",
      "definition": "Application is in. The client is confirming their identity.",
      "nextStep": "Waiting on the client to finish identity verification — a few minutes on their phone.",
      "owner": "buyer",
      "byPartnerType": {
        "realtor": {
          "definition": "Your buyer's application is in and they're confirming their identity.",
          "nextStep": "Waiting on your buyer to finish identity verification — a few minutes on their phone. A nudge from you usually does it.",
          "owner": "buyer"
        },
        "loan_officer": {
          "definition": "Your borrower's application is in and they're confirming their identity.",
          "nextStep": "Waiting on your borrower to finish identity verification — a few minutes on their phone.",
          "owner": "buyer"
        }
      }
    },
    {
      "key": "pending_documents",
      "label": "Gathering Documents",
      "definition": "Identity is confirmed. The client is uploading income and savings documents.",
      "nextStep": "Waiting on the client to upload what Doorly asked for — at their own pace.",
      "owner": "buyer",
      "byPartnerType": {
        "realtor": {
          "definition": "Your buyer is uploading their income and savings documents.",
          "nextStep": "Waiting on your buyer to upload what Doorly asked for. They can see the exact list in their own account.",
          "owner": "buyer"
        },
        "loan_officer": {
          "definition": "Your borrower is uploading the income and savings documents Doorly asked for.",
          "nextStep": "Waiting on your borrower",
          "owner": "buyer"
        }
      }
    },
    {
      "key": "pending_verification",
      "label": "Documents With Doorly",
      "definition": "Everything Doorly asked for is in. Doorly is verifying it.",
      "nextStep": "With the Doorly team — nothing is needed from the client or from you. We'll notify you when it moves.",
      "owner": "doorly",
      "byPartnerType": {
        "realtor": {
          "definition": "Your buyer has given Doorly everything. Doorly is verifying it.",
          "nextStep": "With the Doorly team — nothing is needed from your buyer or from you. We'll notify you when it moves.",
          "owner": "doorly"
        },
        "loan_officer": {
          "definition": "Your borrower has given Doorly everything. Doorly is verifying it.",
          "nextStep": "With the Doorly team — nothing is needed from your borrower or from you. We'll notify you when it moves.",
          "owner": "doorly"
        }
      }
    },
    {
      "key": "pending_credit",
      "label": "Credit Authorization",
      "definition": "Income and savings are verified. The client is authorizing the credit check.",
      "nextStep": "Waiting on the client to authorize the credit check from their own account — only they can give it.",
      "owner": "buyer",
      "note": "On a two-client file, every client authorizes separately from their own account.",
      "byPartnerType": {
        "realtor": {
          "definition": "Your buyer's income and savings are verified. They're authorizing the credit check.",
          "nextStep": "Waiting on your buyer to authorize the credit check from their own account — only they can give it.",
          "owner": "buyer"
        },
        "loan_officer": {
          "definition": "Your borrower's income and savings are verified. They're authorizing the credit check.",
          "nextStep": "Waiting on your borrower to authorize the credit check from their own account. This is the one step you cannot do for them.",
          "owner": "buyer"
        }
      }
    },
    {
      "key": "pending_decision",
      "label": "Decision In Progress",
      "definition": "The credit check is done. Doorly is finishing the decision.",
      "nextStep": "Usually with the Doorly team. If something is needed from your client, they'll see it in their own account. We'll notify you the moment there's a decision.",
      "owner": "doorly",
      "byPartnerType": {
        "realtor": {
          "definition": "Your buyer's credit check is done. Doorly is finishing the decision.",
          "nextStep": "Usually with the Doorly team. If something is needed from your buyer, they'll see it in their own account. We'll notify you the moment there's a decision.",
          "owner": "doorly"
        },
        "loan_officer": {
          "definition": "Your borrower's credit check is done. Doorly is finishing the decision.",
          "nextStep": "Usually with the Doorly team. If something is needed from your borrower, they'll see it in their own account. We'll notify you the moment there's a decision.",
          "owner": "doorly"
        }
      }
    },
    {
      "key": "final_review",
      "label": "Final Review",
      "definition": "The file is with the Doorly team for review.",
      "nextStep": "With the Doorly team — nothing is needed from the client or from you. We'll notify you the moment there's a decision.",
      "owner": "doorly",
      "byPartnerType": {
        "loan_officer": {
          "definition": "Your borrower's file is with the Doorly team for review.",
          "nextStep": "With the Doorly team — nothing is needed from your borrower or from you. We'll notify you the moment there's a decision.",
          "owner": "doorly"
        }
      }
    },
    {
      "key": "approved",
      "label": "Approved",
      "definition": "Buyer is approved and can shop for a home now.",
      "nextStep": "Waiting on the buyer to select a home and request an offer. As fast as they like.",
      "owner": "buyer",
      "note": "Heads up: if your buyer is already under contract on another home, they'll need to terminate that contract before Doorly can submit an offer.",
      "byPartnerType": {
        "loan_officer": {
          "definition": "Your borrower is approved and can shop for a home now.",
          "nextStep": "Waiting on your borrower to choose a home in their own Doorly account and request an offer. As fast as they like.",
          "owner": "buyer"
        },
        "realtor": {
          "nextStep": "Your turn — show them homes and help them choose one, then point them to Homes in their own Doorly account to request the offer. There's no screen on your side — we'll email you as soon as they request it.",
          "owner": "partner"
        },
        "homebuilder": {
          "nextStep": "Your turn — walk them through the homes you have available and help them choose one, then point them to Homes in their own Doorly account to request the offer. There's no screen on your side — we'll email you as soon as they request it.",
          "owner": "partner"
        }
      }
    },
    {
      "key": "offer_accepted",
      "label": "Offer Accepted",
      "definition": "Doorly submitted an offer on the home your buyer chose and the seller accepted it; the purchase contract is being finalized.",
      "nextStep": "Waiting on the buyer to sign the Loan Estimate and approve the home purchase.",
      "owner": "buyer",
      "byPartnerType": {
        "loan_officer": {
          "definition": "Doorly submitted an offer on the home your borrower chose and the seller accepted it; the purchase contract is being finalized.",
          "nextStep": "Waiting on your borrower to sign the Loan Estimate and approve the home purchase.",
          "owner": "buyer"
        },
        "realtor": {
          "nextStep": "Your home-selection work is done — Doorly takes it from here. Stay reachable while the file is live.",
          "owner": "doorly"
        }
      }
    },
    {
      "key": "under_contract",
      "label": "Under Contract",
      "definition": "Contract signed — buyer is in the option period.",
      "nextStep": "Waiting on the buyer to send down payment and order appraisal.",
      "owner": "buyer",
      "byPartnerType": {
        "loan_officer": {
          "definition": "Contract signed — your borrower is in the option period.",
          "nextStep": "Waiting on your borrower to send the down payment and order the appraisal.",
          "owner": "buyer"
        }
      }
    },
    {
      "key": "closing",
      "label": "Closing",
      "definition": "Closing day is scheduled.",
      "nextStep": "Waiting on the buyer to sign their Closing Disclosure and prep closing funds.",
      "owner": "buyer",
      "byPartnerType": {
        "loan_officer": {
          "nextStep": "Waiting on your borrower to sign their Closing Disclosure and prepare closing funds.",
          "owner": "buyer"
        }
      }
    },
    {
      "key": "closed",
      "label": "Closed",
      "definition": "Buyer closed on their home.",
      "nextStep": "Nothing left — the buyer is a homeowner.",
      "owner": "terminal",
      "byPartnerType": {
        "loan_officer": {
          "definition": "Your borrower closed on their home.",
          "nextStep": "Nothing left — your borrower is a homeowner.",
          "owner": "terminal"
        }
      }
    },
    {
      "key": "not_moving_forward",
      "label": "Not Moving Forward",
      "definition": "This buyer isn't moving forward with Doorly right now.",
      "nextStep": "Waiting on the buyer to re-engage — reach out if you'd like to help restart.",
      "owner": "terminal",
      "byPartnerType": {
        "loan_officer": {
          "definition": "This borrower isn't moving forward with Doorly right now.",
          "nextStep": "Waiting on your borrower to re-engage — reach out if you'd like to help restart.",
          "owner": "terminal"
        }
      }
    }
  ];

const withColor = (s) => ({ ...s, color: STATUS_COLORS[s.key] || NEUTRAL_COLOR });

// Mutable module state, seeded from the bootstrap copy and replaced on a
// successful fetch. Subscribers re-render via usePartnerStatuses().
let current = BOOTSTRAP_STATUSES.map(withColor);
let version = 0;
const listeners = new Set();

export const PARTNER_STATUSES = current;

export function getPartnerStatuses() { return current; }

// Fetch backend copy and merge by key. Called once at app boot. Any failure
// leaves the bootstrap copy in place — a knowledge outage must never blank the
// partner's status UI.
export async function hydratePartnerStatuses() {
  try {
    const res = await fetch(`${API_URL}/api/knowledge`);
    if (!res.ok) return false;
    const data = await res.json();
    if (!Array.isArray(data?.partnerStatuses) || data.partnerStatuses.length === 0) return false;
    current = data.partnerStatuses.map(withColor);
    version += 1;
    listeners.forEach((fn) => fn(version));
    return true;
  } catch (_) {
    return false; // offline / endpoint down → keep bootstrap copy
  }
}

export function usePartnerStatuses() {
  const [, setV] = useState(version);
  useEffect(() => {
    const fn = (v) => setV(v);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  return current;
}

// Neutral fallback for a label not in the set — never crash, never surface a raw
// internal value.
export const FALLBACK_STATUS = {
  key: 'unknown',
  label: 'Pending Application',
  definition: "This buyer's file is in progress.",
  nextStep: "Waiting on the buyer to continue. Reach out if you'd like an update.",
  color: NEUTRAL_COLOR,
};

// 🚨 partnerType IS OPTIONAL AND CHANGES "WHO HOLDS THE BALL", NOT WHAT THE
// STATUS MEANS. Under Realtor Services Agreement v2.1 §2.1 the realtor performs
// home selection, so "waiting on the buyer to select a home" is false for them —
// and §4.2 conditions their fee on completing it before Doorly offers. A realtor
// who reads the shared copy and waits is being told to forfeit.
//
// ⚠️ THE OVERRIDE COMES FROM THE BACKEND COPY, NOT FROM HERE. byPartnerType is
// served through /api/knowledge so Eva voices the same override she is talking
// to. This function only selects; it never authors.
//
// ⚠️ ABSENT TYPE OR NO OVERRIDE → THE BASE COPY, UNCHANGED. A partner whose type
// has not loaded yet sees the shared wording rather than nothing, and loan
// officers and homebuilders — whose buyers really do self-serve — are untouched.
export function getStatusMeta(label, partnerType) {
  const match = current.find((s) => s.label === label);
  if (!match) return { ...FALLBACK_STATUS, label: label || FALLBACK_STATUS.label };
  const override = partnerType && match.byPartnerType && match.byPartnerType[partnerType];
  return override ? { ...match, ...override } : match;
}

// ⚠️ KEYED ON THE LABEL STRING, so it moves with the rename. 'Invited' here
// after the rename would have silently made an unaccepted invite count as IN
// progress — a pipeline number that goes up when nothing happened.
export const NOT_IN_PROGRESS_LABELS = ['Invite not yet accepted', 'Closed', 'Not Moving Forward'];

export function isInProgress(partnerStatus) {
  return !NOT_IN_PROGRESS_LABELS.includes(getStatusMeta(partnerStatus).label);
}

// Ordered linear happy-path labels ONLY — excludes 'Not Moving Forward', which
// is an off-ramp, not a step. Computed from live copy so a backend change flows
// through to the journey timeline.
export function getJourneySteps() {
  return current.filter((s) => s.label !== 'Not Moving Forward').map((s) => s.label);
}
export const JOURNEY_STEPS = getJourneySteps();

export function getStatusIndex(label) {
  return getJourneySteps().indexOf(getStatusMeta(label).label);
}
